// src/hooks/useRegistrationCache.ts
//
// Persists registration wizard state to localStorage with a 24-hour expiry.
// File objects (photo, CV, proof) are NOT cached — only serialisable data.
// On remount the hook rehydrates step + account + profile from cache.

import { useCallback, useEffect, useRef } from 'react';
import { registrationData, ProfilePatch } from './auth.service';
import { Step } from '../pages/Register';

const CACHE_KEY = 'tute_register_tutor';
const EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

// Fields that hold File objects — we strip these before caching
const FILE_FIELDS: (keyof ProfilePatch)[] = [
  'profilePhoto',
  'proofFile',
  'CVFile',
];

interface CachedState {
  step: Step;
  account: registrationData;
  profile: ProfilePatch;
  savedAt: number;
}

/** Strip File objects and other non-serialisable values from profile */
function serialisableProfile(profile: ProfilePatch): ProfilePatch {
  const clean = { ...profile };
  for (const key of FILE_FIELDS) {
    delete (clean as any)[key];
  }
  return clean;
}

export function useRegistrationCache(
  step: Step,
  account: registrationData,
  profile: ProfilePatch,
  setStep: (s: Step) => void,
  setAccount: (a: registrationData) => void,
  setProfile: (p: ProfilePatch) => void,
) {
  const hasHydrated = useRef(false);

  // ── Hydrate on mount ──────────────────────────────────────────────
  useEffect(() => {
    if (hasHydrated.current) return;
    hasHydrated.current = true;

    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return;

      const cached: CachedState = JSON.parse(raw);

      // Check expiry
      if (Date.now() - cached.savedAt > EXPIRY_MS) {
        localStorage.removeItem(CACHE_KEY);
        return;
      }

      // Rehydrate state
      if (cached.step) setStep(cached.step);
      if (cached.account) setAccount(cached.account);
      if (cached.profile) setProfile(cached.profile);
    } catch {
      localStorage.removeItem(CACHE_KEY);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist on every change ───────────────────────────────────────
  const save = useCallback(() => {
    const payload: CachedState = {
      step,
      account,
      profile: serialisableProfile(profile),
      savedAt: Date.now(),
    };
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
    } catch {
      // localStorage full or unavailable — silently ignore
    }
  }, [step, account, profile]);

  useEffect(() => {
    save();
  }, [save]);

  // ── Clear cache (call on successful registration) ─────────────────
  const clearCache = useCallback(() => {
    console.log("here")
    localStorage.removeItem(CACHE_KEY);
  }, []);

  return { clearCache };
}